/**
 * 완성보고서 1장 확인 → 승인/반려 (교사 1명 = 1장, 부록 K)
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, IconButton, MenuItem, Radio, RadioGroup, TextField, Tooltip, Typography,
} from '@mui/material';
import { RotateRight, ZoomIn, ZoomOut, OpenInNew } from '@mui/icons-material';
import { api, Checklist, CertData, ClubCode } from '../api';
import { isValidEnName, toEnName, C, CLUBS, certDate, clubLabel, todayYmd, ymdhm } from '../common';
import { CertificateView } from '../certificate/Certificate';
import { StatusChip, ToneChip } from './ui';

type ReportDetail = {
  _id: string; batchId: string; source: string; regNo: string; churchName: string; region?: string; managerName: string; managerPhone: string;
  teacherId?: string | null; teacherName: string; teacherNameEn?: string; club: ClubCode; status: any;
  files: Array<{ url: string; mimeType: string; originalName: string }>;
  requestedDate?: string; requestedDateReason?: string; submittedAt: string; receivedAt?: string; checkingAt?: string; decidedAt?: string;
  rejectReason?: string; checklist: Checklist; archivePath?: string; problems: string[];
  candidates: Array<{ _id: string; teacherName: string; teacherNameEn: string; phoneMasked: string; keyCode: string; clubs: string[] }>;
  allTeachers: Array<{ _id: string; teacherName: string; phoneMasked: string }>;
  attendance: { count: number; last: string | null };
  existingCertificates: CertData[];
  certificate: CertData | null;
  history: Array<{ at: string; action: string; by: string; note?: string }>;
};

/** 승인 조건: 1단계 참석 + 2~4단계·최종 서명 일괄 확인 (서버와 같은 규칙) */
const problemsOf = (c: Checklist) => {
  const p: string[] = [];
  if (!c.step1Attended) p.push('1단계 BT 참석');
  const legacyDone = Number(c.step2Score) >= 80 && (c.step3Verses || []).filter(Boolean).length >= 10
    && (c.step4Visit || c.step4Practice) && c.step4Signed && c.signTeacher && c.signCoordinator;
  if (!c.reportVerified && !legacyDone) p.push('2~4단계·최종 서명 확인');
  return p;
};
const isSpecificClub = (c: string) => CLUBS.some((x) => x.code === c);

const ReportReview: React.FC<{ reportId: string; onChanged: () => void; onNext?: () => void }> = ({ reportId, onChanged, onNext }) => {
  const [d, setD] = useState<ReportDetail | null>(null);
  const [error, setError] = useState('');
  const [check, setCheck] = useState<Checklist>({});
  const [teacherId, setTeacherId] = useState<string | null>(null);
  const [nameEn, setNameEn] = useState('');
  const [club, setClub] = useState<ClubCode | ''>('');
  const [dateMode, setDateMode] = useState<'today' | 'request' | 'custom'>('today');
  const [customDate, setCustomDate] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [fileIdx, setFileIdx] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [rot, setRot] = useState(0);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const dirty = useRef(false);
  const timer = useRef<number>();

  const load = () => api<{ data: ReportDetail }>(`/admin/cert/reports/${reportId}`).then((r) => {
    const x = r.data;
    setD(x);
    const c = x.checklist || {};
    const untouched = !x.checkingAt;
    setCheck({
      ...c,
      step3Verses: Array.from({ length: 10 }, (_, i) => Boolean(c.step3Verses?.[i])),
      // 처음 열 때 출석 기록이 있으면 1단계 자동 체크
      step1Attended: untouched && x.attendance.count > 0 ? true : c.step1Attended,
      step1Auto: untouched && x.attendance.count > 0 ? true : c.step1Auto,
    });
    setTeacherId(x.teacherId || (x.candidates.length === 1 ? x.candidates[0]._id : null));
    setNameEn(x.teacherNameEn || x.candidates.find((t) => t._id === (x.teacherId || x.candidates[0]?._id))?.teacherNameEn || '');
    setClub(isSpecificClub(x.club) ? x.club : ''); // 그룹(구분 없음)으로 들어온 건은 다시 지정하도록 비움
    setDateMode('today'); setCustomDate(''); setCustomReason(''); setFileIdx(0); setZoom(1); setRot(0);
    dirty.current = false;
  }).catch((e) => setError(e.message));

  useEffect(() => { setD(null); setError(''); setNotice(''); load(); }, [reportId]); // eslint-disable-line react-hooks/exhaustive-deps

  const locked = d?.status === 'approved';
  const save = async () => {
    if (!d || locked) return;
    try {
      await api(`/admin/cert/reports/${d._id}/checklist`, { method: 'PUT', body: { checklist: check, teacherId, teacherNameEn: nameEn, club } });
      dirty.current = false;
      onChanged();
    } catch (e: any) { setError(e.message); }
  };
  // 확인 내용 자동 저장 (교회 화면에 '확인 중' 표시)
  useEffect(() => {
    if (!dirty.current) return undefined;
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(save, 900);
    return () => window.clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [check, teacherId, nameEn, club]);
  const upd = (patch: Partial<Checklist>) => { dirty.current = true; setCheck((c) => ({ ...c, ...patch })); };

  const approve = async () => {
    if (!d) return;
    setBusy(true); setError('');
    const completedDate = dateMode === 'request' ? d.requestedDate : dateMode === 'custom' ? customDate : undefined;
    const dateReason = dateMode === 'request' ? `교회 요청: ${d.requestedDateReason || ''}` : dateMode === 'custom' ? customReason : undefined;
    try {
      const r = await api<{ data: { certificate: CertData; archivePath: string } }>(`/admin/cert/reports/${d._id}/approve`, {
        body: { checklist: check, teacherId, teacherNameEn: nameEn, club, completedDate, dateReason },
      });
      setNotice(`승인 완료 · 수료증 ${r.data.certificate.serial} 발급${r.data.archivePath ? ` · 보관: bt-reports/${r.data.archivePath}` : ''}`);
      onChanged();
      load();
    } catch (e: any) { setError(e.message); } finally { setBusy(false); }
  };
  const reject = async () => {
    try {
      await api(`/admin/cert/reports/${d!._id}/reject`, { body: { reason: rejectReason } });
      setRejectOpen(false); setRejectReason(''); onChanged(); load();
    } catch (e: any) { setError(e.message); }
  };
  const hideReport = async () => {
    if (!window.confirm('이 보고서를 목록에서 삭제할까요? 교회 제출 현황에서도 사라집니다. (발급된 수료증·보관함 파일은 그대로)')) return;
    try { await api('/admin/cert/reports/hide', { body: { ids: [d!._id] } }); onChanged(); onNext?.(); } catch (e: any) { setError(e.message); }
  };
  const cancelApproval = async () => {
    const reason = window.prompt('승인을 취소합니다. 수료증 번호는 다시 쓰지 않습니다. 사유를 입력해주세요.');
    if (!reason) return;
    try { await api(`/admin/cert/reports/${d!._id}/cancel-approval`, { body: { reason } }); onChanged(); load(); } catch (e: any) { setError(e.message); }
  };

  if (error && !d) return <Alert severity="error">{error}</Alert>;
  if (!d) return <Box sx={{ py: 10, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;
  const problems = [...(club ? [] : ['클럽 지정']), ...(isValidEnName(nameEn) ? [] : ['영문 이름']), ...problemsOf(check)];
  const file = d.files[fileIdx];
  const dateOk = dateMode !== 'custom' || (customDate && customReason.trim());
  const teacherOptions = [...d.candidates.map((c) => ({ _id: c._id, label: `${c.teacherName} · ${c.phoneMasked} · ${c.keyCode}` })),
    ...d.allTeachers.filter((t) => !d.candidates.some((c) => c._id === t._id)).map((t) => ({ _id: t._id, label: `${t.teacherName} · ${t.phoneMasked}` }))];

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.1fr 1fr' }, gap: 2 }}>
      {/* 보고서 이미지 */}
      <Box sx={{ border: `1px solid ${C.line}`, borderRadius: 2, bgcolor: '#2b2b2b', position: 'relative', minHeight: 520, overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, display: 'flex', gap: 0.5, bgcolor: 'rgba(255,255,255,.9)', borderRadius: 2 }}>
          <Tooltip title="축소"><IconButton size="small" onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}><ZoomOut fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="확대"><IconButton size="small" onClick={() => setZoom((z) => Math.min(3, z + 0.25))}><ZoomIn fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="회전"><IconButton size="small" onClick={() => setRot((r) => (r + 90) % 360)}><RotateRight fontSize="small" /></IconButton></Tooltip>
          {file && <Tooltip title="새 창"><IconButton size="small" onClick={() => window.open(file.url, '_blank')}><OpenInNew fontSize="small" /></IconButton></Tooltip>}
        </Box>
        {d.files.length > 1 && (
          <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 2, display: 'flex', gap: 0.5 }}>
            {d.files.map((_, i) => <Chip key={i} size="small" label={`${i + 1}`} onClick={() => setFileIdx(i)} sx={{ bgcolor: i === fileIdx ? '#ffe066' : '#fff' }} />)}
          </Box>
        )}
        <Box sx={{ height: { xs: 520, lg: 'calc(100vh - 260px)' }, overflow: 'auto', display: 'grid', placeItems: 'center' }}>
          {file && (file.mimeType === 'application/pdf'
            ? <iframe title="보고서" src={file.url} style={{ width: '100%', height: '100%', border: 0, background: '#fff' }} />
            : /hei[cf]/.test(file.mimeType)
              ? <Box sx={{ color: '#fff', p: 3, textAlign: 'center' }}>HEIC 사진은 브라우저에서 미리보기가 안 됩니다.<br /><Button sx={{ mt: 1 }} variant="contained" onClick={() => window.open(file.url, '_blank')}>내려받아 보기</Button></Box>
              : <img src={file.url} alt="완성보고서" style={{ width: `${zoom * 100}%`, transform: `rotate(${rot}deg)`, transition: 'transform .2s' }} />)}
        </Box>
      </Box>

      {/* 확인 체크리스트 */}
      <Box sx={{ display: 'grid', gap: 1.5, alignContent: 'start' }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography fontSize={20} fontWeight={800}>{d.teacherName}</Typography>
          <StatusChip status={d.status} />
          {d.source === 'email' && <ToneChip tone="info" label="이메일 접수" />}
        </Box>
        <Typography fontSize={13} color={C.sub}>
          {d.churchName} ({d.regNo}{d.region ? ` · ${d.region}` : ''}) · 담당 {d.managerName} {d.managerPhone} · 제출 {ymdhm(d.submittedAt)}
        </Typography>
        {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
        {notice && <Alert severity="success">{notice}</Alert>}
        {d.status === 'rejected' && <Alert severity="error">반려됨: {d.rejectReason}</Alert>}

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
          <Autocomplete size="small" disabled={locked} options={teacherOptions} value={teacherOptions.find((o) => o._id === teacherId) || null}
            onChange={(_, v) => { dirty.current = true; setTeacherId(v?._id || null); const cand = d.candidates.find((c) => c._id === v?._id); if (cand?.teacherNameEn && !nameEn) setNameEn(cand.teacherNameEn); }}
            isOptionEqualToValue={(a, b) => a._id === b._id}
            renderInput={(p) => <TextField {...p} label="교사 데이터 연결" helperText={teacherId ? '교사 이력에 수료가 기록됩니다' : '연결 안 하면 이름으로만 발급'} />} />
          <TextField select size="small" label="클럽 (필수)" required disabled={locked} value={club} error={!locked && !club}
            helperText={!locked && !club ? '클럽을 하나로 지정해주세요' : ''} onChange={(e) => { dirty.current = true; setClub(e.target.value as ClubCode); }}>
            {CLUBS.map((c) => <MenuItem key={c.code} value={c.code}>{c.label} ({String(c.no).padStart(2, '0')})</MenuItem>)}
          </TextField>
        </Box>
        <TextField required size="small" label="영문 이름 (없으면 교사 정보에 저장)" disabled={locked} value={nameEn} placeholder="HONG GILDONG"
          error={!isValidEnName(nameEn)} helperText={isValidEnName(nameEn) ? '' : '영문 이름이 있어야 승인할 수 있습니다 (영어 대문자)'}
          onChange={(e) => { dirty.current = true; setNameEn(toEnName(e.target.value)); }} />

        <Step n={1} title="BT 참석" ok={Boolean(check.step1Attended)}>
          <FormControlLabel disabled={locked} control={<Checkbox checked={Boolean(check.step1Attended)} onChange={(e) => upd({ step1Attended: e.target.checked, step1Auto: false })} />}
            label={check.step1Auto ? `출석 기록으로 자동 확인 (${d.attendance.count}건${d.attendance.last ? ` · 최근 ${ymdhm(d.attendance.last)}` : ''})` : d.attendance.count ? `출석 기록 ${d.attendance.count}건` : '출석 기록 없음 — 보고서로 확인 후 체크'} />
        </Step>
        <Step n={2} title="2~4단계 · 최종 서명 (보고서 이미지로 한 번에 확인)" ok={Boolean(check.reportVerified) || !problemsOf({ ...check, step1Attended: true }).length}>
          <Typography fontSize={12.5} color={C.sub} mb={0.5}>
            왼쪽 완성보고서에서 아래 내용을 보고 이상이 없으면 한 번만 체크하세요.
          </Typography>
          <Box component="ul" sx={{ m: 0, mb: 0.75, pl: 2.25, color: C.sub, fontSize: 12.5, lineHeight: 1.7 }}>
            <li><b>2단계 BT Test</b> — 80점 이상 · 날짜 · 사인</li>
            <li><b>3단계 성경구절 암송</b> — Awana 주제성구 딤후 2:15 · Awana의 기도 · 복음의 수레바퀴 핵심 성경구절 8개 (날짜 ″ 표시 인정) · 사인</li>
            <li><b>4단계 견학 또는 실습</b> — 둘 중 하나 이상 · 날짜 · 사인</li>
            <li><b>최종 서명</b> — 본인 사인 · 조정관/감독관 사인 · 날짜</li>
            <li>보고서 위쪽 <b>영문 이름</b>이 위 영문 이름 칸과 같은지</li>
          </Box>
          <FormControlLabel disabled={locked} sx={{ bgcolor: check.reportVerified ? '#fff3cf' : '#fff', border: `1px solid ${check.reportVerified ? '#f0cf5a' : C.line}`, borderRadius: 2, pr: 1.5, ml: 0 }}
            control={<Checkbox checked={Boolean(check.reportVerified)} onChange={(e) => upd({ reportVerified: e.target.checked })} />}
            label={<Typography fontWeight={800} fontSize={14}>보고서 확인 완료 (2~4단계 · 최종 서명)</Typography>} />
        </Step>
        <TextField size="small" label="메모 (내부용)" disabled={locked} value={check.memo || ''} onChange={(e) => upd({ memo: e.target.value })} />

        {!locked && (
          <Box sx={{ p: 1.5, border: `1px solid ${C.line}`, borderRadius: 2, bgcolor: '#fff' }}>
            <Typography fontWeight={800} fontSize={14}>수료증 날짜</Typography>
            <RadioGroup value={dateMode} onChange={(e) => setDateMode(e.target.value as any)}>
              <FormControlLabel value="today" control={<Radio size="small" />} label={`승인일 (오늘 ${certDate(new Date())})`} />
              {d.requestedDate && <FormControlLabel value="request" control={<Radio size="small" />}
                label={<span>교회 요청 <b style={{ color: C.warn }}>{certDate(d.requestedDate + 'T12:00:00+09:00')}</b> — {d.requestedDateReason}</span>} />}
              <FormControlLabel value="custom" control={<Radio size="small" />} label="다른 날짜로 지정" />
            </RadioGroup>
            {dateMode === 'custom' && (
              <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                <TextField size="small" type="date" value={customDate} inputProps={{ max: todayYmd() }} onChange={(e) => setCustomDate(e.target.value)} />
                <TextField size="small" placeholder="사유 (필수)" value={customReason} onChange={(e) => setCustomReason(e.target.value)} sx={{ flex: 1 }} />
              </Box>
            )}
          </Box>
        )}

        {locked && d.certificate ? (
          <Box>
            <Box sx={{ border: `1px solid ${C.line}`, borderRadius: 1, overflow: 'hidden' }}><CertificateView cert={d.certificate} fluid /></Box>
            <Typography fontSize={12.5} color={C.sub} mt={0.75}>📁 보관: bt-reports/{d.archivePath || '-'}</Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button variant="contained" onClick={() => window.open(`/admin/bt-cert/print?ids=${d.certificate!._id}`, '_blank')}>인쇄 / PDF</Button>
              <Button color="error" onClick={cancelApproval}>승인 취소</Button>
              <Button onClick={hideReport}>목록에서 삭제</Button>
              {onNext && <Button onClick={onNext}>다음 보고서 ›</Button>}
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button variant="contained" size="large" disabled={problems.length > 0 || busy || !dateOk} onClick={approve}>
              {busy ? '발급 중…' : `승인 · ${club ? clubLabel(club) + ' ' : ''}수료증 발급`}
            </Button>
            <Button variant="outlined" color="error" onClick={() => setRejectOpen(true)}>반려</Button>
            <Button onClick={hideReport}>삭제</Button>
            {onNext && <Button onClick={onNext}>다음 ›</Button>}
            {problems.length > 0 && <Typography fontSize={12.5} color={C.warn}>남은 항목: {problems.join(', ')}</Typography>}
          </Box>
        )}
        {d.existingCertificates.filter((c) => c.status === 'issued' && c._id !== d.certificate?._id).length > 0 && (
          <Alert severity="info">이미 보유한 수료증: {d.existingCertificates.filter((c) => c.status === 'issued').map((c) => `${c.serial}(${c.clubLabel})`).join(', ')}</Alert>
        )}
        <Box>
          <Typography fontSize={12} fontWeight={800} color={C.sub}>처리 기록</Typography>
          {d.history.map((h, i) => <Typography key={i} fontSize={12} color={C.sub}>{ymdhm(h.at)} · {h.action} · {h.by}{h.note ? ` · ${h.note}` : ''}</Typography>)}
        </Box>
      </Box>

      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle fontWeight={800}>반려 사유</DialogTitle>
        <DialogContent>
          <Typography fontSize={13} color={C.sub} mb={1}>교회 화면에 그대로 보이고 ‘다시 올리기’ 버튼이 나타납니다.</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
            {['사진이 흐려서 확인이 어렵습니다', '2단계 점수가 비어 있습니다', '서명이 빠졌습니다', '보고서 일부가 잘렸습니다'].map((r) => (
              <Chip key={r} size="small" label={r} onClick={() => setRejectReason(r)} />
            ))}
          </Box>
          <TextField fullWidth multiline minRows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
        </DialogContent>
        <DialogActions><Button onClick={() => setRejectOpen(false)}>닫기</Button><Button variant="contained" disabled={!rejectReason.trim()} onClick={reject}>반려</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

const Step: React.FC<{ n: number; title: string; ok: boolean; warn?: string; action?: React.ReactNode; children: React.ReactNode }> = ({ n, title, ok, warn, action, children }) => (
  <Box sx={{ p: 1.25, border: `1px solid ${ok ? '#f0d77a' : C.line}`, bgcolor: ok ? '#fffbea' : '#fff', borderRadius: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ width: 22, height: 22, borderRadius: '50%', display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 800, bgcolor: ok ? '#f5b800' : '#eee', color: ok ? '#3b2f00' : '#777' }}>{ok ? '✓' : n}</Box>
      <Typography fontWeight={800} fontSize={14} sx={{ flex: 1 }}>{n <= 4 && !/^\d/.test(title) ? `${n}단계 ` : ''}{title}</Typography>
      {warn && <Typography fontSize={12} color={C.warn} fontWeight={700}>{warn}</Typography>}
      {action}
    </Box>
    <Box sx={{ pl: 3.5, pt: 0.5 }}>{children}</Box>
  </Box>
);

export default ReportReview;
